import React, { useEffect, useState } from 'react'
import s from './FeatureVisuals.module.scss'

// Animated count-up that runs once `active` flips true.
const useCountUp = (end: number, active: boolean, duration = 1500) => {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (!active) return
    let raf = 0
    let startTs: number | null = null
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts
      const progress = Math.min((ts - startTs) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * end))
      if (progress < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [end, active, duration])
  return value
}

/* --------------------------- Code editor --------------------------- */
export const CodeEditorVisual = () => (
  <div className={s.window}>
    <div className={s.winBar}>
      <span className={s.dot} />
      <span className={s.dot} />
      <span className={s.dot} />
      <span className={s.winFile}>socommerz.ts</span>
    </div>
    <div className={s.code}>
      <div className={s.row}>
        <span className={`${s.bar} ${s.kw}`} style={{ width: 46 }} />
        <span className={`${s.bar} ${s.fn}`} style={{ width: 90 }} />
        <span className={`${s.bar} ${s.dim}`} style={{ width: 30 }} />
      </div>
      <div className={s.row}>
        <span className={`${s.bar} ${s.dim}`} style={{ width: 24 }} />
        <span className={`${s.bar} ${s.str}`} style={{ width: 120 }} />
      </div>
      <div className={s.row}>
        <span className={`${s.bar} ${s.fn}`} style={{ width: 64 }} />
        <span className={`${s.bar} ${s.dim}`} style={{ width: 44 }} />
        <span className={`${s.bar} ${s.kw}`} style={{ width: 38 }} />
      </div>
      <div className={s.row}>
        <span className={`${s.bar} ${s.dim}`} style={{ width: 40 }} />
        <span className={`${s.bar} ${s.str}`} style={{ width: 70 }} />
        <span className={s.cursor} />
      </div>
    </div>
    <div className={s.avatars}>
      <span className={s.avatar} />
      <span className={s.avatar} />
      <span className={s.avatar} />
      <span className={s.avatar} />
      <span className={s.online}>4 engineers online</span>
    </div>
  </div>
)

/* --------------------------- Usage panel --------------------------- */
const BAR_HEIGHTS = [30, 52, 40, 68, 48, 80, 60, 92, 72, 96, 64, 84]

export const UsageVisual = ({
  active,
  end = 50,
}: {
  active: boolean
  end?: number
}) => {
  const value = useCountUp(end, active)
  return (
    <div className={`${s.panel} ${active ? s.filled : ''}`}>
      <div className={s.planRow}>
        <span className={s.planIcon} />
        <div>
          <div className={s.planName}>OpenAI wrappers</div>
          <div className={s.planSub}>Shipped to production</div>
        </div>
      </div>
      <div className={s.bigStat}>{value}+</div>
      <div className={s.meterLabel}>Across Retail, B2B &amp; creative lines</div>
      <div className={s.meter}>
        <span className={s.meterFill} />
      </div>
      <div className={s.chart}>
        {BAR_HEIGHTS.map((h, i) => (
          <span
            className={s.chartBar}
            key={i}
            style={{
              height: active ? `${h}%` : '8%',
              transitionDelay: `${i * 55}ms`,
            }}
          />
        ))}
      </div>
    </div>
  )
}

/* ------------------------------ Chat ------------------------------- */
export const ChatVisual = () => (
  <div className={s.chat}>
    <div className={`${s.bubble} ${s.user}`}>
      Recommend cosy basics in size M?
    </div>
    <div className={`${s.bubble} ${s.ai}`}>
      Absolutely — here are a few comfy essentials:
    </div>
    <div className={s.miniProducts}>
      <div className={s.miniProd}>
        <div className={s.miniThumb} />
        <div className={s.miniName}>Deluxe Shirt</div>
        <div className={s.miniPrice}>₹2,470</div>
      </div>
      <div className={s.miniProd}>
        <div className={s.miniThumb} />
        <div className={s.miniName}>Essential Hoodie</div>
        <div className={s.miniPrice}>₹4,560</div>
      </div>
    </div>
    <div className={s.typing}>
      <span />
      <span />
      <span />
    </div>
  </div>
)

/* --------------------------- Storefront ---------------------------- */
export const StorefrontVisual = () => (
  <div className={s.store}>
    {[
      { name: 'Retail POS', meta: 'Module · live' },
      { name: 'Catalog', meta: 'Module · live' },
      { name: 'Checkout', meta: 'Module · live' },
      { name: 'Analytics', meta: 'Module · live' },
    ].map((p) => (
      <div className={s.product} key={p.name}>
        <div className={s.prodThumb} />
        <div className={s.prodName}>{p.name}</div>
        <div className={s.prodMeta}>{p.meta}</div>
        <div className={s.prodBtn}>Deploy</div>
      </div>
    ))}
  </div>
)

/* --------------------------- Flow diagram -------------------------- */
const CHIP_W = 100

const FLOW_CHIPS = [
  { x: 4, y: 28, label: 'Legacy DB', color: '#94a3b8' },
  { x: 4, y: 116, label: 'APIs', color: '#94a3b8' },
  { x: 130, y: 78, label: 'Pipeline', color: '#2665ff', hub: true },
  { x: 256, y: 28, label: 'Warehouse', color: '#36b1f2' },
  { x: 256, y: 116, label: 'Apps', color: '#a855f7' },
]

const FLOW_PATHS = [
  'M104 48 C120 48 114 100 130 100',
  'M104 136 C120 136 114 100 130 100',
  'M230 100 C246 100 240 48 256 48',
  'M230 100 C246 100 240 136 256 136',
]

export const FlowVisual = () => (
  <div className={s.flow}>
    <svg viewBox='0 0 360 184' preserveAspectRatio='xMidYMid meet'>
      <defs>
        <linearGradient id='flowGrad' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0%' stopColor='#2665ff' />
          <stop offset='100%' stopColor='#36b1f2' />
        </linearGradient>
      </defs>

      {/* connectors */}
      {FLOW_PATHS.map((d, i) => (
        <path key={`c${i}`} className={s.conn} d={d} />
      ))}

      {/* data packets travelling along the connectors */}
      {FLOW_PATHS.map((d, i) => (
        <circle key={`p${i}`} r='3.2' fill='url(#flowGrad)'>
          <animateMotion
            dur='1.8s'
            begin={`${i * 0.45}s`}
            repeatCount='indefinite'
            path={d}
          />
        </circle>
      ))}

      {/* chips */}
      {FLOW_CHIPS.map((c, i) => (
        <g key={`chip${i}`}>
          <rect
            className={c.hub ? s.chipHub : s.chip}
            x={c.x}
            y={c.y}
            width={CHIP_W}
            height={c.hub ? 44 : 40}
            rx='11'
          />
          <circle cx={c.x + 17} cy={c.y + (c.hub ? 22 : 20)} r='5' fill={c.color} />
          <text
            className={c.hub ? s.chipLabelHub : s.chipLabel}
            x={c.x + 30}
            y={c.y + (c.hub ? 22 : 20)}
          >
            {c.label}
          </text>
        </g>
      ))}
    </svg>
  </div>
)

/* ---------------------------- Pipeline ----------------------------- */
const PIPE_ICONS: Record<string, React.ReactNode> = {
  build: <path d='M8 6 3 12l5 6M16 6l5 6-5 6' />,
  operate: <path d='M3 12h4l3 7 4-14 3 7h4' />,
  transfer: <path d='M4 8h13l-4-4M20 16H7l4 4' />,
}

const PIPE_STEPS = [
  { icon: 'build', name: 'Build', desc: 'Robust, scalable apps' },
  { icon: 'operate', name: 'Operate', desc: 'Smooth daily ops' },
  { icon: 'transfer', name: 'Transfer', desc: 'Controlled handover' },
]

export const PipelineVisual = () => (
  <div className={s.pipeline}>
    {PIPE_STEPS.map((step, i) => (
      <React.Fragment key={step.name}>
        <div className={s.step}>
          <div className={s.stepIcon}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              {PIPE_ICONS[step.icon]}
            </svg>
          </div>
          <div className={s.stepText}>
            <div className={s.stepName}>{step.name}</div>
            <div className={s.stepDesc}>{step.desc}</div>
          </div>
        </div>
        {i < PIPE_STEPS.length - 1 && (
          <div className={s.connector} aria-hidden='true'>
            <span className={s.chev} />
            <span className={s.chev} />
            <span className={s.chev} />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
)
